import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

export function Card({ children, className }) {
  return (
    <div className={clsx('rounded-xl border border-slate-200 bg-white p-6 shadow-sm', className)}>
      {children}
    </div>
  );
}

export function CardHeader({ title, description, action }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {description && <p className="mt-1 text-sm text-slate-600">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatCard({ label, value, hint, accent = 'sky' }) {
  const accentRing =
    accent === 'rose'    ? 'ring-rose-200    bg-rose-50    text-rose-700'    :
    accent === 'emerald' ? 'ring-emerald-200 bg-emerald-50 text-emerald-700' :
    accent === 'amber'   ? 'ring-amber-200   bg-amber-50   text-amber-700'   :
                            'ring-sky-200     bg-sky-50     text-sky-700';

  return (
    <Card>
      <div className={clsx('inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] ring-1 ring-inset', accentRing)}>
        {label}
      </div>
      <div className="mt-3 text-3xl font-semibold tracking-tight text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </Card>
  );
}

export function QuickAction({ to, title, description, icon: Icon }) {
  return (
    <Link
      to={to}
      className="group flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-5 transition hover:border-slate-300 hover:shadow-sm"
    >
      {Icon && (
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900">{title}</div>
        <p className="mt-1 text-sm text-slate-600">{description}</p>
      </div>
      <ArrowRight className="mt-1 h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-slate-700" />
    </Link>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
      <div className="mx-auto max-w-md">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {description && <p className="mt-1 text-sm text-slate-600">{description}</p>}
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}

export function ErrorPanel({ error, onRetry }) {
  const { t } = useTranslation();
  const status = error?.response?.status;
  const msg    = error?.response?.data?.message || error?.message || t('common.unknownError');
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800">
      <div className="font-semibold">
        {t('table.loadErrorData')}{status ? ` (HTTP ${status})` : ''}.
      </div>
      <div className="mt-1">{msg}</div>
      {onRetry && (
        <button type="button" onClick={onRetry} className="mt-3 rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-800 hover:bg-rose-100">
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}
